
import { blogPostsApi, categoriesApi, uploadApi } from "@/integrations/mysql/client";
import { BlogPost, BlogPostFormData, BlogCategory } from "@/types/blogPost";
import { embedVideoInContent } from "@/utils/videoContent";

export const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  return blogPostsApi.list({}) as Promise<BlogPost[]>;
};

export const fetchPublishedBlogPosts = async (): Promise<BlogPost[]> => {
  return blogPostsApi.list({ published: true }) as Promise<BlogPost[]>;
};

export const fetchFeaturedBlogPost = async (): Promise<BlogPost | null> => {
  const post = await blogPostsApi.featured();
  return (post as BlogPost | null);
};

export const fetchBlogPostsByCategory = async (categoryId: string): Promise<BlogPost[]> => {
  return blogPostsApi.list({ published: true, category_id: categoryId }) as Promise<BlogPost[]>;
};

export const fetchRelatedBlogPosts = async (postId: string, categoryId: string, limit: number = 3): Promise<BlogPost[]> => {
  return blogPostsApi.list({ published: true, category_id: categoryId, exclude_id: postId, limit }) as Promise<BlogPost[]>;
};

export const fetchBlogCategories = async (): Promise<BlogCategory[]> => {
  return categoriesApi.list() as Promise<BlogCategory[]>;
};

/** Calcula tempo de leitura em minutos com base no texto do post (200 wpm). */
function calcReadTime(content: string): number {
  const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const words = text ? text.split(' ').length : 0;
  return Math.max(1, Math.ceil(words / 200));
}

/** Parses content_formatted whether it is a JSON string (TEXT column) or already an object */
function parseContentFormatted(raw: any): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  if (typeof raw === "object") return raw as Record<string, any>;
  return {};
}

function packContentFormatted(formData: BlogPostFormData): any {
  const base = parseContentFormatted(formData.content_formatted);
  if (formData.video_url) return { ...base, _video_url: formData.video_url };
  // Remove _video_url key if URL is empty
  const { _video_url: _, ...rest } = base;
  return rest;
}

function resolvePublishState(formData: BlogPostFormData): { published: boolean; scheduled_for: string | null } {
  // A scheduled post is always saved as published=true so the server releases it at the right time.
  // Without a schedule, respect the toggle as-is.
  return {
    published: !!formData.scheduled_for || formData.published || false,
    scheduled_for: formData.scheduled_for ?? null,
  };
}

export const createBlogPost = async (formData: BlogPostFormData): Promise<void> => {
  const content = embedVideoInContent(formData.content || '', formData.video_url || '');
  const { published, scheduled_for } = resolvePublishState(formData);
  await blogPostsApi.create({
    title: formData.title,
    subtitle: formData.subtitle,
    content,
    content_formatted: formData.content_formatted,
    excerpt: formData.excerpt,
    featured_image_url: formData.featured_image_url,
    category_id: formData.category_id,
    published,
    scheduled_for,
    read_time_minutes: calcReadTime(formData.content || ''),
    is_featured: false,
    author_id: '',
    created_at: '',
    updated_at: '',
  });
};

export const updateBlogPost = async (postId: string, formData: BlogPostFormData): Promise<void> => {
  const content = embedVideoInContent(formData.content || '', formData.video_url || '');
  const { published, scheduled_for } = resolvePublishState(formData);
  await blogPostsApi.update(postId, {
    title: formData.title,
    subtitle: formData.subtitle,
    content,
    content_formatted: formData.content_formatted,
    excerpt: formData.excerpt,
    featured_image_url: formData.featured_image_url,
    category_id: formData.category_id,
    published,
    scheduled_for,
    read_time_minutes: calcReadTime(formData.content || ''),
  });
};

export const deleteBlogPost = async (postId: string): Promise<void> => {
  await blogPostsApi.delete(postId);
};

/** Compresses an image file to stay under maxBytes using Canvas API. */
async function compressImage(file: File, maxBytes = 4.5 * 1024 * 1024): Promise<File> {
  if (file.size <= maxBytes) return file;

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);

      // Scale down if very large
      const MAX_DIM = 1920;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width >= height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM; }
        else                 { width  = Math.round((width  * MAX_DIM) / height); height = MAX_DIM; }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);

      // Try progressively lower quality until under limit
      let quality = 0.85;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error("Falha ao comprimir imagem.")); return; }
            if (blob.size <= maxBytes || quality <= 0.3) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          "image/jpeg",
          quality
        );
      };
      tryCompress();
    };
    img.onerror = () => reject(new Error("Falha ao ler imagem."));
    img.src = url;
  });
}

export const uploadBlogImage = async (file: File): Promise<string> => {
  const compressed = await compressImage(file);
  return uploadApi.image(compressed);
};
